import React, { useState, useEffect } from 'react';
import adminApi from '../../../api/adminApi';
import '../../../assets/css/admin/profile.css';

const AdminProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  const [adminData, setAdminData] = useState({
    name: '', email: '', role: '', phone: '', location: '',
    joinDate: '', department: '', employeeId: '', bio: '', avatar: ''
  });

  const [statsData, setStatsData] = useState({
    total_patients: 0, active_therapists: 0, sessions_this_month: 0, revenue: 0
  });

  const [activities, setActivities] = useState([]);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    fetchProfile();
    fetchStats();
    fetchActivities();
    fetchPermissions();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await adminApi.get('/profile');
      setAdminData({
        name: res.data.name,
        email: res.data.email,
        role: res.data.role,
        phone: res.data.phone || '',
        location: res.data.location || '',
        joinDate: new Date(res.data.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        department: res.data.department || '',
        employeeId: res.data.employee_id || '',
        bio: res.data.bio || '',
        avatar: res.data.avatar || ''
      });
    } catch (err) {
      console.error('Profile fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await adminApi.get('/dashboard/stats');
      setStatsData(res.data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    }
  };

  const fetchActivities = async () => {
    try {
      const res = await adminApi.get('/dashboard/activities');
      setActivities(res.data);
    } catch (err) {
      console.error('Activities fetch error:', err);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await adminApi.get('/dashboard/permissions');
      setPermissions(res.data);
    } catch (err) {
      console.error('Permissions fetch error:', err);
    }
  };

  const stats = [
    {
      label: 'Total Patients', value: statsData.total_patients.toLocaleString(),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), color: 'var(--admin-blue, #0284c7)', bgColor: 'var(--admin-blue-pale, #e0f2fe)'
    },
    {
      label: 'Active Therapists', value: statsData.active_therapists.toLocaleString(),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12 15V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 18H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), color: 'var(--admin-navy, #1a237e)', bgColor: 'var(--admin-navy-soft, #e8eaf6)'
    },
    {
      label: 'Sessions This Month', value: statsData.sessions_this_month.toLocaleString(),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), color: 'var(--admin-green, #16a34a)', bgColor: 'var(--admin-green-pale, #dcfce7)'
    },
    {
      label: 'Revenue (PKR)', value: Number(statsData.revenue).toLocaleString(),
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2V22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ), color: 'var(--admin-orange, #ff7a00)', bgColor: 'var(--admin-orange-pale, #fff4e6)'
    },
  ];

  const handleEdit = () => setIsEditing(!isEditing);

  const handleSave = async () => {
    try {
      await adminApi.put('/profile', {
        name: adminData.name,
        email: adminData.email,
        phone: adminData.phone,
        location: adminData.location,
        department: adminData.department,
        bio: adminData.bio,
      });
      setIsEditing(false);
      fetchActivities();
      alert('Profile updated successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed');
    }
  };

  const handleInputChange = (field, value) => {
    setAdminData({ ...adminData, [field]: value });
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('avatar', file);

    try {
      const res = await adminApi.post('/profile/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAdminData({ ...adminData, avatar: res.data.avatar_url });
    } catch (err) {
      alert(err.response?.data?.message || 'Photo upload failed');
    }
  };

  if (loading) {
    return <div className="admin-profile-container"><p>Loading...</p></div>;
  }

  return (
    <div className="admin-profile-container">
      <div className="admin-profile-overview-card">
        <div className="admin-profile-header-section">
          <div className="admin-avatar-container">
            <div className="admin-avatar-large">
              {adminData.avatar ? (
                <img src={adminData.avatar} alt={adminData.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <span className="admin-avatar-initials">
                  {adminData.name.split(' ').map(n => n[0]).join('')}
                </span>
              )}
            </div>
            <label className="admin-avatar-upload-btn" style={{ cursor: 'pointer' }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 4V12M4 8H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Update Photo
              <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
            </label>
          </div>

          <div className="admin-profile-details">
            <div className="admin-profile-name-section">
              <h1 className="admin-name">{adminData.name}</h1>
              <span className="admin-role-pill">{adminData.role}</span>
            </div>
            <p className="admin-email">{adminData.email}</p>

            <div className="admin-quick-info-grid">
              <div className="admin-quick-info-item">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M3.75 5.25L9 9L14.25 5.25M3.75 12.75H14.25C14.6642 12.75 15 12.4142 15 12V6C15 5.58579 14.6642 5.25 14.25 5.25H3.75C3.33579 5.25 3 5.58579 3 6V12C3 12.4142 3.33579 12.75 3.75 12.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{adminData.email}</span>
              </div>
              <div className="admin-quick-info-item">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M3.75 7.5C3.75 4.73858 5.98858 2.5 8.75 2.5C11.5114 2.5 13.75 4.73858 13.75 7.5C13.75 10.2614 11.5114 12.5 8.75 12.5C5.98858 12.5 3.75 10.2614 3.75 7.5Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M8.75 15.5C6.12665 15.5 4 13.3733 4 10.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>{adminData.phone}</span>
              </div>
              <div className="admin-quick-info-item">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 9.75C10.2426 9.75 11.25 8.74264 11.25 7.5C11.25 6.25736 10.2426 5.25 9 5.25C7.75736 5.25 6.75 6.25736 6.75 7.5C6.75 8.74264 7.75736 9.75 9 9.75Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M14.25 7.5C14.25 11.6421 9 15.75 9 15.75C9 15.75 3.75 11.6421 3.75 7.5C3.75 4.60051 6.10051 2.25 9 2.25C11.8995 2.25 14.25 4.60051 14.25 7.5Z" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{adminData.location}</span>
              </div>
              <div className="admin-quick-info-item">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M13.5 3H4.5C3.67157 3 3 3.67157 3 4.5V13.5C3 14.3284 3.67157 15 4.5 15H13.5C14.3284 15 15 14.3284 15 13.5V4.5C15 3.67157 14.3284 3 13.5 3Z" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M12 1.5V4.5M6 1.5V4.5M3 7.5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                <span>Joined {adminData.joinDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-profile-stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="admin-profile-stat-card">
            <div className="admin-profile-stat-card-content">
              <div className="admin-profile-stat-header">
                <div className="admin-profile-stat-icon-box" style={{ backgroundColor: stat.bgColor }}>
                  <div style={{ color: stat.color }}>{stat.icon}</div>
                </div>
              </div>
              <div className="admin-profile-stat-details">
                <div className="admin-profile-stat-value">{stat.value}</div>
                <div className="admin-profile-stat-label">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="admin-tabs-container">
        <div className="admin-tabs-navigation">
          <button className={`admin-tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
            Overview
          </button>
          <button className={`admin-tab-button ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
            Personal Details
          </button>
          <button className={`admin-tab-button ${activeTab === 'activity' ? 'active' : ''}`} onClick={() => setActiveTab('activity')}>
            Activity Log
          </button>
          <button className={`admin-tab-button ${activeTab === 'permissions' ? 'active' : ''}`} onClick={() => setActiveTab('permissions')}>
            Permissions
          </button>
        </div>

        <div className="admin-tab-content">
          {activeTab === 'overview' && (
            <div className="admin-overview-section">
              <div className="admin-professional-grid">
                <div className="admin-professional-card">
                  <div className="admin-card-header-modern"><h3>Personal Information</h3></div>
                  <div className="admin-card-body-modern">
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Full Name</span>
                        <span className="admin-info-item-value">{adminData.name}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Email Address</span>
                        <span className="admin-info-item-value">{adminData.email}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Phone Number</span>
                        <span className="admin-info-item-value">{adminData.phone}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Location</span>
                        <span className="admin-info-item-value">{adminData.location}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-professional-card">
                  <div className="admin-card-header-modern"><h3>Professional Information</h3></div>
                  <div className="admin-card-body-modern">
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Role</span>
                        <span className="admin-info-item-value">{adminData.role}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Department</span>
                        <span className="admin-info-item-value">{adminData.department}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Employee ID</span>
                        <span className="admin-info-item-value">{adminData.employeeId}</span>
                      </div>
                    </div>
                    <div className="admin-modern-info-item">
                      <div className="admin-info-item-content">
                        <span className="admin-info-item-label">Join Date</span>
                        <span className="admin-info-item-value">{adminData.joinDate}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'details' && (
            <div className="admin-details-section">
              <div className="admin-info-card">
                <div className="admin-info-card-header">
                  <h3>Complete Profile Details</h3>
                  <button className="admin-edit-save-btn" onClick={isEditing ? handleSave : handleEdit}>
                    {isEditing ? 'Save' : 'Edit'}
                  </button>
                </div>
                <div className="admin-info-card-body">
                  {isEditing ? (
                    <div className="admin-edit-form">
                      <div className="admin-form-grid">
                        <div className="admin-form-field">
                          <label>Full Name</label>
                          <input type="text" value={adminData.name} onChange={(e) => handleInputChange('name', e.target.value)} className="admin-form-input" />
                        </div>
                        <div className="admin-form-field">
                          <label>Email Address</label>
                          <input type="email" value={adminData.email} onChange={(e) => handleInputChange('email', e.target.value)} className="admin-form-input" />
                        </div>
                        <div className="admin-form-field">
                          <label>Phone Number</label>
                          <input type="tel" value={adminData.phone} onChange={(e) => handleInputChange('phone', e.target.value)} className="admin-form-input" />
                        </div>
                        <div className="admin-form-field">
                          <label>Location</label>
                          <input type="text" value={adminData.location} onChange={(e) => handleInputChange('location', e.target.value)} className="admin-form-input" />
                        </div>
                        <div className="admin-form-field">
                          <label>Department</label>
                          <input type="text" value={adminData.department} onChange={(e) => handleInputChange('department', e.target.value)} className="admin-form-input" />
                        </div>
                        <div className="admin-form-field">
                          <label>Employee ID</label>
                          <input type="text" value={adminData.employeeId} disabled className="admin-form-input disabled" />
                        </div>
                      </div>
                      <div className="admin-form-field full-width">
                        <label>Bio</label>
                        <textarea value={adminData.bio} onChange={(e) => handleInputChange('bio', e.target.value)} className="admin-form-textarea" rows="4" />
                      </div>
                    </div>
                  ) : (
                    <div className="admin-details-grid">
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Email Address</span>
                        <span className="admin-detail-value">{adminData.email}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Phone Number</span>
                        <span className="admin-detail-value">{adminData.phone}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Location</span>
                        <span className="admin-detail-value">{adminData.location}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Department</span>
                        <span className="admin-detail-value">{adminData.department}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Employee ID</span>
                        <span className="admin-detail-value">{adminData.employeeId}</span>
                      </div>
                      <div className="admin-detail-row">
                        <span className="admin-detail-label">Join Date</span>
                        <span className="admin-detail-value">{adminData.joinDate}</span>
                      </div>
                      <div className="admin-detail-row full-width">
                        <span className="admin-detail-label">Biography</span>
                        <span className="admin-detail-value">{adminData.bio}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="admin-activity-section">
              <div className="admin-info-card">
                <div className="admin-info-card-header"><h3>Recent Activity</h3></div>
                <div className="admin-info-card-body">
                  <div className="admin-activity-list">
                    {activities.length === 0 && <p style={{ color: 'var(--admin-gray-500, #64748b)' }}>No recent activity.</p>}
                    {activities.map((activity) => (
                      <div key={activity.id} className="admin-activity-item">
                        <div className={`admin-activity-indicator ${activity.type}`}></div>
                        <div className="admin-activity-content">
                          <p className="admin-activity-action">{activity.action}</p>
                          <div className="admin-activity-meta">
                            <span className="admin-activity-target">{activity.target_name}</span>
                            <span className="admin-activity-separator">•</span>
                            <span className="admin-activity-time">{new Date(activity.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="admin-permissions-section">
              <div className="admin-info-card">
                <div className="admin-info-card-header"><h3>System Access & Permissions</h3></div>
                <div className="admin-info-card-body">
                  <div className="admin-permissions-table">
                    <div className="admin-permissions-header">
                      <div className="admin-permission-col">Module</div>
                      <div className="admin-permission-col">Read</div>
                      <div className="admin-permission-col">Write</div>
                      <div className="admin-permission-col">Delete</div>
                    </div>
                    {permissions.map((perm, index) => (
                      <div key={index} className="admin-permissions-row">
                        <div className="admin-permission-col module-name">{perm.module}</div>
                        <div className="admin-permission-col">
                          <span className={`admin-permission-status ${perm.read ? 'granted' : 'denied'}`}>{perm.read ? '✓' : '✕'}</span>
                        </div>
                        <div className="admin-permission-col">
                          <span className={`admin-permission-status ${perm.write ? 'granted' : 'denied'}`}>{perm.write ? '✓' : '✕'}</span>
                        </div>
                        <div className="admin-permission-col">
                          <span className={`admin-permission-status ${perm.delete ? 'granted' : 'denied'}`}>{perm.delete ? '✓' : '✕'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;